import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useContext, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { PortalContext } from '../../context/PortalContext';
import { useCoverageType } from '../../hooks/useCoverageType/useCoverageType';
import { useLagoon } from '../../hooks/useLagoon';
import { usePrimaryCare } from '../../hooks/usePrimaryCare';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { usePCPStore } from '../../store/usePCPStore';
import { PCPSearchStore } from '../../store/usePCPStore/pcpStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import {
  doChoosePCPStoreUpdate,
  getFuturePcpValues,
  isCnsRestrictedEditPcp,
} from '../../utils/primaryCare.utils';
import { getCurrentMemberData } from '../../utils/providerSearch.utils';
import {
  getLoggedInMember,
  getPolicyNumber,
  getReportingCode,
} from '../../utils/user.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { ANALYTICS_CONSTANTS } from '../CareTeamCard/CareTeamUtils';
import { CareTeamProviderCard } from '../CareTeamProviderCard';
import {
  CareCategories,
  ProviderTypes,
  ReverseCoverageTypesCodes,
} from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { ConstantsRoutes } from '../ConstantsRoutes';
import { ThingsToConsiderModal } from '../ThingsToConsiderModal';
import { getFeatureFlag } from '../Utils';
import { addDaysToDate, dateFormatUTC } from '../Utils/datesUtils/formatDate';
import {
  isCnsMember,
  isEniMember,
  isIfpMember,
  isUmrMember,
} from '../Utils/memberUtils/memberUtils';
import {
  ChoosePCPButton,
  ChoosePCPCardContainer,
  ChoosePCPCnsOrIFP,
  InfoIconStyle,
  InfoPopoverContainerStyle,
  InfoPopoverListStyle,
} from './CareTeamEmptyState.styled';

const getMemberPcpStatus = (
  memberPCPInfo: any,
  isKelseySeyboldPlan: boolean,
  isSanitasPlan: boolean,
  sanitasPCP: string,
  lockInIndicator: any,
  isCnsReportingCode: boolean
) => {
  const hasCurrentPCP = memberPCPInfo != null && memberPCPInfo?.hasPCP === true;
  const hasFuturePCP =
    memberPCPInfo != null && memberPCPInfo?.futurePCP != null;

  const isSanitasPlanMember =
    isSanitasPlan && memberPCPInfo?.futurePCP?.pcpID === sanitasPCP;
  const isKelseySeyboldPlanMember =
    isKelseySeyboldPlan &&
    memberPCPInfo?.futurePCP?.providerType?.toLowerCase() ===
      ProviderTypes.MEDICAL_GROUP.toLowerCase();

  const hasRestriction =
    isSanitasPlanMember ||
    isKelseySeyboldPlanMember ||
    lockInIndicator ||
    isCnsReportingCode;

  const isSanitasOrKelseySeyboldOrLockInWithFuturePCP =
    hasRestriction && !hasCurrentPCP && hasFuturePCP;
  const isSanitasOrKelseySeyboldOrLockInWithoutPCP =
    hasRestriction && !hasCurrentPCP && !hasFuturePCP;
  const memberWithFuturePCP = !hasCurrentPCP && hasFuturePCP;
  const memberWithoutPCP = !hasCurrentPCP && !hasFuturePCP;

  return {
    hasCurrentPCP,
    hasFuturePCP,
    isSanitasPlanMember,
    isKelseySeyboldPlanMember,
    hasRestriction,
    isSanitasOrKelseySeyboldOrLockInWithFuturePCP,
    isSanitasOrKelseySeyboldOrLockInWithoutPCP,
    memberWithFuturePCP,
    memberWithoutPCP,
  };
};

const getProviderId = (
  providerId: string,
  pcpData: any,
  isUspNiceMember: boolean,
  isFuturePcp: boolean
) =>
  isUspNiceMember
    ? pcpData?.providerId
    : isFuturePcp
    ? pcpData?.providerID || pcpData?.providerId
    : providerId || pcpData?.providerId;

const getCardMessageInfo = (
  statusInfo: ReturnType<typeof getMemberPcpStatus>,
  memberPCPInfo: any,
  pcpConfig: any,
  futurePcpMessage: any,
  noPcpMessage: any,
  noProviderTitle: any,
  choosePcpByDate: string,
  isPreEffective: boolean
) => {
  const loggedInMemberInfo = getLoggedInMember();
  const isIfp = isIfpMember(loggedInMemberInfo?.population);
  const isIfpPreEffective = isIfp && isPreEffective;
  const {
    isSanitasOrKelseySeyboldOrLockInWithFuturePCP,
    isSanitasOrKelseySeyboldOrLockInWithoutPCP,
    memberWithFuturePCP,
    memberWithoutPCP,
  } = statusInfo;

  const futurePCPMessage =
    isSanitasOrKelseySeyboldOrLockInWithFuturePCP || memberWithFuturePCP
      ? getFuturePcpValues(
          futurePcpMessage?.message || '',
          memberPCPInfo?.futurePCP
        )
      : '';

  const renderMessageWithDate = (message: string): React.ReactNode => {
    if (!message?.includes('{{date}}')) return message;
    const replacementValue = isIfpPreEffective
      ? choosePcpByDate
      : `1/1/${new Date().getFullYear() + 1}`;
    const parts = message.split(/by\s*\{\{date\}\}/);
    if (parts.length === 2) {
      return (
        <>
          {parts[0]}
          <strong>by {replacementValue}</strong>
          {parts[1]}
        </>
      );
    }
    return message;
  };

  return {
    futurePCPMessage,
    shouldShowFuturePCPMessage:
      isSanitasOrKelseySeyboldOrLockInWithFuturePCP || memberWithFuturePCP,
    shouldShowNoPCPMessage:
      isSanitasOrKelseySeyboldOrLockInWithoutPCP || memberWithoutPCP,
    noPCPMessage: pcpConfig
      ? renderMessageWithDate(pcpConfig?.pcpMessage)
      : noPcpMessage?.message,
    defaultMessage: pcpConfig?.pcpMessage
      ? renderMessageWithDate(pcpConfig?.pcpMessage)
      : noProviderTitle?.message,
  };
};

const renderInfoPopoverContent = (infoDetails: any) => {
  const infoData = infoDetails?.message?.split('<li>');
  if (!infoData || (Array.isArray(infoData) && infoData.length === 0))
    return null;
  return (
    <>
      <Text fontSize="sm">{infoData[0]}</Text>
      <ul style={InfoPopoverContainerStyle}>
        {infoData.map((item: string, index: number) => {
          if (index === 0) {
            return null;
          }
          return (
            <li key={index} style={InfoPopoverListStyle}>
              {item}
            </li>
          );
        })}
      </ul>
    </>
  );
};

const shouldShowChooseButton = (hasRestriction: boolean, editPcp: boolean) =>
  !hasRestriction && editPcp;

const getResolvedProviderName = ({
  isStaffModelMember,
  isFuturePcp,
  memberPCPInfo,
  pcpData,
}: {
  isStaffModelMember: boolean;
  isFuturePcp: boolean;
  memberPCPInfo: any;
  pcpData: any;
}) => {
  if (isStaffModelMember) {
    return (
      (isFuturePcp
        ? memberPCPInfo?.futurePCP?.pcpName
        : memberPCPInfo?.pcpName) || pcpData?.providerName
    );
  }

  return (
    pcpData?.providerName ||
    (isFuturePcp ? pcpData?.pcpName : memberPCPInfo?.pcpName)
  );
};

const renderEmptyStateMessage = (
  messageInfo: ReturnType<typeof getCardMessageInfo>,
  showYellowPCPRequiredCard: boolean,
  dateDisplay: React.ReactNode,
  pcpWillBeAssignedSubscriber: any,
  infoDetails: any
) => {
  if (messageInfo.shouldShowFuturePCPMessage) {
    return (
      <Text
        color="black"
        css={{
          textAlign: 'center',
          marginBottom: '10px',
          marginTop: '32px',
        }}
        data-auto-testid="future-pcp-effective-message"
        data-testid="future-pcp-effective-message"
        fontSize="md"
      >
        {messageInfo.futurePCPMessage}
      </Text>
    );
  }

  if (messageInfo.shouldShowNoPCPMessage) {
    return (
      <Text
        color="black"
        css={{
          textAlign: 'center',
          marginBottom: '10px',
          marginTop: '32px',
        }}
        data-auto-testid="no-pcp-message"
        data-testid="no-pcp-message"
        fontSize="md"
      >
        {messageInfo.noPCPMessage}
      </Text>
    );
  }

  if (showYellowPCPRequiredCard) {
    return (
      <ChoosePCPCnsOrIFP
        data-auto-testid="choose-pcp-by-date"
        data-testid="choose-pcp-by-date"
      >
        {dateDisplay} {pcpWillBeAssignedSubscriber?.message ?? ''}
      </ChoosePCPCnsOrIFP>
    );
  }

  return (
    <Text
      color="black"
      css={{
        display: 'ruby',
        textAlign: 'center',
        marginBottom: '10px',
      }}
      data-auto-testid="care-team-empty-text"
      data-testid="care-team-empty-text"
      fontSize="md"
    >
      {messageInfo.defaultMessage}
      <Popover
        content={renderInfoPopoverContent(infoDetails)}
        data-auto-testid={'care-info-icon-popover'}
        data-testid={'care-info-icon-popover'}
        title={infoDetails?.title}
      >
        <IconSymbol
          css={InfoIconStyle}
          data-auto-testid={'care-info-icon'}
          data-testid={'care-info-icon'}
          icon="info"
          size={16}
          variant="outlined"
        />
      </Popover>
    </Text>
  );
};

export const ChoosePCPCard = ({
  dependentSeqNbr,
  isPreEffective,
  pcpData,
  isFuturePcp = false,
  pcpConfig = null as any,
  isStaffModelMember = false,
  isStaffModelPCPInNetwork,
}) => {
  const { data } = usePrimaryCare();
  const { primaryCare } = data;
  const lagoonData = useLagoon('ui-messaging')();
  const featureFlags = useLagoon('feature-flags')();
  const customMessages = useLagoon('custom-messages')();
  const allConfigs = useLagoon('config');
  const [showModal, setShowModal] = useState(false);
  const { portalData } = useContext(PortalContext);
  const { navigate } = useRouter();

  const displayThingsToConsiderForEni = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_THINGS_TO_CONSIDER_MODAL
  );

  const loggedInMemberInfo = getLoggedInMember();
  const currentMember = getCurrentMemberData(dependentSeqNbr);
  const { demographics, eligibility, lineOfBusiness, isGatedUser, population } =
    loggedInMemberInfo || {};
  const isUmr = isUmrMember(portalData?.portalName);

  const isCns = isCnsMember(lineOfBusiness);
  const isIfp = isIfpMember(population);

  const coverageType = useCoverageType();

  const memberPolicyNumber = getPolicyNumber(currentMember, coverageType);
  const memberReportingCode = getReportingCode(currentMember, coverageType);

  const memberPCPInfo = primaryCare?.find(
    ({ depSeqNbr }) => dependentSeqNbr === depSeqNbr
  )?.pcpInfo;

  const {
    pcpID = '',
    providerID,
    hasPCP,
    futurePCP,
  } = isFuturePcp ? memberPCPInfo?.futurePCP || {} : memberPCPInfo || {};

  const sanitasPCP = allConfigs()?.find(
    (row) => row.key === 'SANITAS_PCPID'
  )?.value;

  const { isKelseySeyboldPlan, isSanitasPlan, editPcp } = currentMember;

  const lockInIndicator = primaryCare?.[0]?.lockInIndicator;

  const isCnsReportingCode =
    isCns &&
    isCnsRestrictedEditPcp(
      customMessages,
      memberReportingCode,
      memberPolicyNumber
    );

  const memberStatus = getMemberPcpStatus(
    memberPCPInfo,
    isKelseySeyboldPlan,
    isSanitasPlan,
    sanitasPCP,
    lockInIndicator,
    isCnsReportingCode
  );

  const { hasRestriction } = memberStatus;

  const showThingsToConsider = () =>
    !isUmr && isEniMember(lineOfBusiness) && displayThingsToConsiderForEni;
  const { setTypeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { setPCPSearchState } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      setPCPSearchState: state.setPCPSearchState,
      primarySpeciality: state.pcpSearchState.primarySpeciality,
    }))
  );

  const openThingsToConsiderModal = () => {
    setShowModal(true);
  };

  const onAddPCPClick = () => {
    const urlParams = doChoosePCPStoreUpdate(
      demographics?.dependentSeqNbr,
      { ...chipStore, chipValue: CareCategories.PRIMARY_CARE },
      setTypeaheadSearchStore,
      setPCPSearchState
    );
    navigate(`${ConstantsRoutes.PROVIDER_SEARCH_RESULTS.path}${urlParams}`);
  };

  const noProviderTitle = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.CARE_TEAM.CARE_NO_PROVIDER_DESC,
  });

  const infoDetails = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.CARE_TEAM.CARE_CHOOSE_PCP_INFO,
  });

  const futurePcpMessage = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.CARE_TEAM.FUTURE_PCP_EFFECTIVE_DATE,
  });

  const noPcpMessage = find(lagoonData, {
    key: ConstantsLagoon.PRIMARY_CARE.PCP_NOT_REQUIRED_INFO,
  });

  const handleContinue = () => {
    setShowModal(false);
    onAddPCPClick();
  };

  const closeThingsToConsiderModal = () => {
    setShowModal(false);
  };

  const pcpWillBeAssignedSubscriber = find(lagoonData, {
    key: ConstantsLagoon.PRIMARY_CARE.PCP_WILL_BE_ASSIGNED_SUBSCRIBER,
  });

  const choosePcpSoonCareTeam = find(lagoonData, {
    key: ConstantsLagoon.PRIMARY_CARE.CHOOSE_PCP_SOON_CARE_TEAM,
  });

  const choosePcpByCareTeam = find(lagoonData, {
    key: ConstantsLagoon.PRIMARY_CARE.CHOOSE_PCP_BY_CARE_TEAM,
  });

  const enableNotRequiredPcpInfo = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NOT_REQUIRED_PCP_INFO
  );

  const enableStaffModelEni = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_ALL_STAFF_MODEL_ENI
  );

  const isUspNiceMember = enableStaffModelEni && currentMember?.isUspNiceMember;

  // Compute date and card condition for CNS/IFP
  const coverageMedical =
    eligibility?.[0]?.memberHealthCoverage?.coverageType?.find(
      (x) => x?.typeCode?.coverageTypeCode === 'M'
    );
  const coverageStartDate = dateFormatUTC(
    coverageMedical?.coverageEffDateRange?.startDate
  );
  const choosePcpByDate = addDaysToDate(new Date(coverageStartDate), 30);

  const messageInfo = getCardMessageInfo(
    memberStatus,
    memberPCPInfo,
    pcpConfig,
    futurePcpMessage,
    noPcpMessage,
    noProviderTitle,
    choosePcpByDate,
    isPreEffective
  );

  const dateDisplay = isCns ? (
    <>{choosePcpSoonCareTeam?.message}</>
  ) : (
    <>
      {choosePcpByCareTeam?.message} <strong>{choosePcpByDate}</strong>
    </>
  );

  const showYellowPCPRequiredCard =
    (!enableNotRequiredPcpInfo && isIfp && isPreEffective) ||
    (isCns && isGatedUser);

  const choosePcpButton = find(lagoonData, {
    key: ConstantsLagoon.PRIMARY_CARE.CHOOSE_PCP_BUTTON,
  });

  const viewIdCardButton = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.VIEW_ID_CARD_BUTTON,
  });

  const handleViewIdCard = () => {
    window.location.href =
      viewIdCardButton?.url || '/community/dashboard/modal/id-cards';
  };

  const handleChoosePcpClick = () => {
    adobeLinkTrackEvent({
      destinationUrl: '',
      name: 'choose-pcp',
      location: `body:${ANALYTICS_CONSTANTS.PCP_SNACK_CARD}`,
      type: 'internal',
    });

    if (showThingsToConsider()) {
      openThingsToConsiderModal();
      return;
    }

    onAddPCPClick();
  };

  const resolvedProviderName = getResolvedProviderName({
    isStaffModelMember,
    isFuturePcp,
    memberPCPInfo,
    pcpData,
  });

  return (
    <React.Fragment>
      {(pcpID || (isFuturePcp && pcpData?.pcpID)) && pcpData ? (
        <CareTeamProviderCard
          coverageType={ReverseCoverageTypesCodes.MEDICAL}
          data-auto-testid={
            isFuturePcp ? 'future-pcp-provider-card' : 'care-team-provider-card'
          }
          data-testid={
            isFuturePcp ? 'future-pcp-provider-card' : 'care-team-provider-card'
          }
          dependentSeqNbr={dependentSeqNbr}
          futurePCP={isFuturePcp ? pcpData : futurePCP}
          imgSrc={pcpData?.imageLocation}
          isFuturePcpCard={isFuturePcp}
          isPcP={isFuturePcp ? false : hasPCP}
          isPreEffective={isPreEffective}
          isStaffModelMember={isStaffModelMember}
          isStaffModelPCPInNetwork={isStaffModelPCPInNetwork}
          isTieredPlan={
            pcpData?.providerLocations &&
            pcpData?.providerLocations[0]?.tieredProvider
          }
          isTieredProvider={pcpData?.isTieredProvider}
          llmCodes={pcpData?.llmCodes}
          locationId={pcpData?.providerLocations?.[0]?.locationId}
          organizationType={pcpData?.organizationType}
          pcpConfig={pcpConfig}
          pcpEffectiveEndDate={
            isFuturePcp
              ? memberPCPInfo?.futurePCP?.endDate
              : memberPCPInfo?.endDate
          }
          pcpEffectiveStartDate={
            isFuturePcp
              ? memberPCPInfo?.futurePCP?.startDate
              : memberPCPInfo?.startDate
          }
          pcpHasSingleLocation={pcpData?.providerLocations?.length === 1}
          professionalDesignation={pcpData?.primaryDegrees?.[0]}
          providerId={getProviderId(
            providerID,
            pcpData,
            isUspNiceMember,
            isFuturePcp
          )}
          providerName={resolvedProviderName}
          providerType={pcpData?.providerType}
          showEffectiveEndDate={!isFuturePcp && pcpConfig?.showEffectiveEndDate}
          showEffectiveStartDate={
            isFuturePcp && pcpConfig?.showEffectiveStartDate
          }
          showPcpLocation={pcpData?.providerLocations?.length > 1}
          snackCardProvider={pcpData}
          speciality={pcpData?.specialities
            ?.map((speciality) => speciality)
            .join(', ')}
        />
      ) : (
        <ChoosePCPCardContainer
          alignContent="center"
          alignItems="center"
          data-auto-testid={'care-choose-pcp-card'}
          data-testid={'care-choose-pcp-card'}
          direction="column"
        >
          <IconSymbol
            color="$greyBorderColor"
            data-auto-testid="care-account-circle"
            data-testid="care-account-circle"
            icon="account_circle"
            size="128"
          />
          {renderEmptyStateMessage(
            messageInfo,
            showYellowPCPRequiredCard,
            dateDisplay,
            pcpWillBeAssignedSubscriber,
            infoDetails
          )}
          {pcpConfig?.viewIDLink && messageInfo.shouldShowNoPCPMessage && (
            <Link
              css={{
                marginTop: '8px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
              data-testid="view-id-card-link"
              href={
                viewIdCardButton?.url ||
                '#/community/dashboard/modal/id-cards?goBackOnClose=1'
              }
              onClick={handleViewIdCard}
            >
              {viewIdCardButton?.message}
              <IconSymbol icon="chevron_right" size="20" />
            </Link>
          )}
          {shouldShowChooseButton(hasRestriction, editPcp) && (
            <ChoosePCPButton
              data-auto-testid={'choose-pcp'}
              data-testid="choose-pcp-button"
              onClick={handleChoosePcpClick}
            >
              {`${choosePcpButton?.message}`}
            </ChoosePCPButton>
          )}
          {showModal && (
            <ThingsToConsiderModal
              editPcpClicked={false}
              isOpen={showModal}
              onClose={closeThingsToConsiderModal}
              onContinue={handleContinue}
            />
          )}
        </ChoosePCPCardContainer>
      )}
    </React.Fragment>
  );
};
